/* eslint-disable react/prop-types */
import React, { useRef } from 'react';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import { Box, Skeleton, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
} from 'chart.js';
import palette from 'theme/customColors';
import { useIntl } from 'react-intl';
import { Download, ShareButton } from 'components/Download';

// Registrar los módulos necesarios para Chart.js
ChartJS.register(
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale
);

/**
 * Componente que muestra un gráfico de línea con el consumo mensual más alto.
 * @param {Object} props - Propiedades del componente
 * @param {string} props.title - Título del gráfico
 * @param {Object} props.monthMostConsume - Datos del mes con mayor consumo
 * @param {number} props.year - Año de referencia
 */
export default function MostConsumeChart({ title, monthMostConsume, year }) {
  const intl = useIntl();
  const chartRef = useRef();

  // Definir nombres de los meses utilizando las traducciones
  const months = [
    intl.formatMessage({ id: 'app.months.jan' }),
    intl.formatMessage({ id: 'app.months.feb' }),
    intl.formatMessage({ id: 'app.months.mar' }),
    intl.formatMessage({ id: 'app.months.apr' }),
    intl.formatMessage({ id: 'app.months.may' }),
    intl.formatMessage({ id: 'app.months.jun' }),
    intl.formatMessage({ id: 'app.months.jul' }),
    intl.formatMessage({ id: 'app.months.ago' }),
    intl.formatMessage({ id: 'app.months.sep' }),
    intl.formatMessage({ id: 'app.months.oct' }),
    intl.formatMessage({ id: 'app.months.nov' }),
    intl.formatMessage({ id: 'app.months.dec' }),
  ];

  // Verificar si `monthMostConsume` y `months_consumes` son válidos
  const hasData =
    monthMostConsume && Array.isArray(monthMostConsume.months_consumes);

  // Configuración de los datos del gráfico de línea
  const data = {
    labels: months, // Meses en el eje X
    datasets: [
      {
        label: `${year}`, // Año como etiqueta
        data: hasData
          ? monthMostConsume.months_consumes
          : new Array(12).fill(0), // Datos de consumo por mes
        fill: false,
        borderColor: '#C69100', // Color de la línea
        backgroundColor: '#C69100', // Color de los puntos
        pointRadius: 4, // Tamaño de los puntos en la línea
        pointHoverRadius: 6, // Tamaño de los puntos al pasar el cursor
        tension: 0.3, // Suaviza la curva de la línea
      },
    ],
  };

  // Opciones de configuración del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }, // Ocultar leyenda
      tooltip: {
        callbacks: {
          label: function (context) {
            let value = context.raw || 0;
            return `${value.toLocaleString()} kWh`; // Formato de miles en el tooltip
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function (value) {
            return `${value.toLocaleString()}`; // Formato de miles en el eje Y
          },
        },
      },
    },
  };

  return (
    <div style={{ height: '100%' }} ref={chartRef}>
      <Portlet>
        {/* Encabezado del gráfico */}
        <PortletHeader noDivider>
          {title ? (
            <PortletLabel title={title} />
          ) : (
            <Skeleton variant="text" width={200} height={50} />
          )}
          <PortletToolbar style={{ display: 'flex', gap: 8 }}>
            <Download itemRef={chartRef} name={title} />
            <ShareButton itemRef={chartRef} name={title} />
          </PortletToolbar>
        </PortletHeader>

        <PortletContent>
          {hasData ? (
            <>
              {/* Mostrar el mes de mayor consumo y su valor */}
              <Box display="flex" justifyContent="center">
                <Box
                  display="flex"
                  flexDirection="row"
                  gap="0.5rem"
                  textAlign="center"
                  p={2}
                  pt={0}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: '#730065', textTransform: 'uppercase' }}
                  >
                    {monthMostConsume.month_name || 'N/A'}
                  </Typography>
                  <Typography variant="h5" sx={{ color: palette.danger.main }}>
                    {monthMostConsume.consume?.toLocaleString() || '0'} kWh
                  </Typography>
                </Box>
              </Box>

              {/* Contenedor del gráfico de línea */}
              <Box height={400} position="relative">
                <Line data={data} options={options} />
              </Box>
            </>
          ) : (
            // Si no hay datos, mostrar un skeleton de carga
            <Skeleton variant="rectangular" height={400} />
          )}
        </PortletContent>
      </Portlet>
    </div>
  );
}
